import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { Plus } from "lucide-react";
import { ApiClient, ApiRequestError } from "@/data/api";
import { createQueryClient, persistCache, restoreCache } from "@/data/cache";
import { DeskProvider } from "@/data/context";
import { useBootstrap } from "@/data/hooks";
import { outbox } from "@/data/outbox";
import { signOutCloud } from "@/data/auth-client";
import { clearCloudSession, clearPairing, resolveSession, type Session } from "@/data/session";
import { useLiveEvents } from "@/data/events";
import { TooltipProvider } from "@/components/ui/misc";
import { Button } from "@/components/ui/button";
import { ConceptProvider, useConcept } from "@/features/concept-provider";
import { useSidebarModel } from "@/features/sidebar/useSidebarModel";
import { ContainerActions, ContainerContextMenu, NewContainerDialog } from "@/features/containers/ContainerActions";
import { DeskDndProvider } from "@/features/dnd/DeskDnd";
import { sidebarDnd } from "@/features/dnd/sidebarDnd";
import { SyncStatus, ConflictDialog } from "@/features/sync/SyncStatus";
import { UpdateReady } from "@/features/desktop/UpdateReady";
import { QuickAdd } from "@/features/quick-add/QuickAdd";
import { CommandPalette } from "@/features/command/CommandPalette";
import { QuickMenu } from "@/features/keyboard/QuickMenu";
import { ShortcutsDialog } from "@/features/keyboard/ShortcutsDialog";
import { useGlobalShortcuts } from "@/features/keyboard/useGlobalShortcuts";
import { useWebNotifications } from "@/features/notifications/useWebNotifications";
import { SignInScreen } from "@/features/pairing/SignInScreen";
import { MacWelcome, useNeedsWelcome } from "@/features/pairing/MacWelcome";
import { TaskDetailOverlay, useDetailPanel } from "@/features/task/TaskDetailHost";
import { BulkBar } from "@/features/selection/BulkBar";
import { useReconcileSelection } from "@/features/selection/selection";
import { openQuickAdd, ui } from "@/features/ui-state";
import { TodayScreen } from "@/features/screens/TodayScreen";
import { AllTasksScreen, AreaScreen, InboxScreen, ProjectScreen, SpaceScreen } from "@/features/screens/ContainerScreens";
import { ReviewScreen, SearchScreen, SnoozedScreen, UpcomingScreen } from "@/features/screens/ListScreens";
import { SettingsScreen } from "@/features/screens/SettingsScreen";
import { TaskRoute } from "@/features/screens/TaskRoute";
import { Toolbar } from "@/features/toolbar/Toolbar";
import { useStored } from "@/lib/storage";
import { DEFAULTS, PREF } from "@/lib/prefs";
import { missingContainer, rememberRoute, takeRestoredLaunch } from "@/features/navigation/launch";
import { useScrollRestoration } from "@/features/navigation/scroll";
import { getLocation, href, navigate, sameRoute, useLocation, type Route } from "@/router";
import { cn } from "@/lib/utils";

function Screen({ route }: { route: Route }) {
  switch (route.name) {
    case "today":
      return <TodayScreen />;
    case "inbox":
      return <InboxScreen />;
    case "upcoming":
      return <UpcomingScreen />;
    case "review":
      return <ReviewScreen />;
    case "snoozed":
      return <SnoozedScreen />;
    case "all":
      return <AllTasksScreen />;
    case "area":
      return <AreaScreen key={route.id} id={route.id} />;
    case "space":
      return <SpaceScreen key={route.id} id={route.id} />;
    case "project":
      return <ProjectScreen key={route.id} id={route.id} />;
    case "task":
      return <TaskRoute readableId={route.readableId} />;
    case "settings":
      return <SettingsScreen />;
    case "search":
      return <SearchScreen />;
  }
}

function NewAreaButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-7 w-full items-center gap-2 rounded-md px-2 text-[13px] text-foreground/45 hover:bg-sidebar-accent hover:text-foreground"
      >
        <Plus className="size-3.5" />
        New area
      </button>
      <NewContainerDialog kind="area" open={open} onOpenChange={setOpen} />
    </>
  );
}

function Workspace({ onUnauthorized }: { onUnauthorized: () => void }) {
  const { concept } = useConcept();
  const loc = useLocation();
  const sidebar = useSidebarModel();
  const boot = useBootstrap();
  const detailPanel = useDetailPanel();
  const [storedCollapsed, setCollapsed] = useStored("sidebar.collapsed", false);
  // Narrow windows start with the sidebar hidden; the toggle still opens it.
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  const [narrowOpen, setNarrowOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 760px)");
    const on = () => (setNarrow(mq.matches), setNarrowOpen(false));
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const collapsed = narrow ? !narrowOpen : storedCollapsed;
  // Settings → Appearance week start and time format: formatting reads them directly, so screens
  // remount when they change.
  const [weekStart] = useStored(PREF.weekStart, DEFAULTS.weekStart);
  const [timeFormat] = useStored(PREF.timeFormat, DEFAULTS.timeFormat);
  // The narrow sidebar floats over the content, so Escape dismisses it like any other overlay.
  useEffect(() => {
    if (!narrow || !narrowOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setNarrowOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [narrow, narrowOpen]);
  useLiveEvents();
  useGlobalShortcuts();
  useReconcileSelection(concept.id);
  useWebNotifications();

  useEffect(() => {
    if (boot.error instanceof ApiRequestError && boot.error.code === "unauthorized") onUnauthorized();
  }, [boot.error, onUnauthorized]);

  // Close detail + clear selection when navigating between screens. A #/task/<id> link
  // redirects to the task's container and opens it there, so that redirect keeps it open.
  const prevRoute = useRef(loc.route.name);
  useEffect(() => {
    const fromTaskLink = prevRoute.current === "task";
    prevRoute.current = loc.route.name;
    if (loc.route.name !== "task" && !fromTaskLink)
      ui.set({
        selectedTaskId: null,
        selectedIds: [],
        selectionAnchor: null,
        openTaskId: ui.get().detailVia === "overlay" ? ui.get().openTaskId : null,
      });
    setNarrowOpen(false);
  }, [loc.path]);
  useEffect(() => rememberRoute(loc), [loc]);
  useScrollRestoration(loc.path);

  // "Open to: Last view" restored a container that has since been archived or deleted: go to Today.
  useEffect(() => {
    // Wait for a fetch from the API: the persisted cache may predate the archive.
    if (!boot.data || !boot.isFetchedAfterMount) return;
    const restored = takeRestoredLaunch();
    const lists = { area: boot.data.areas, space: boot.data.spaces, project: boot.data.projects };
    const has = (kind: keyof typeof lists, id: string) => lists[kind].some((c) => c.id === id && !c.archivedAt);
    if (restored && sameRoute(getLocation().route, restored) && missingContainer(restored, has)) {
      navigate({ name: "today" }, undefined, { replace: true });
    }
  }, [boot.data, boot.isFetchedAfterMount]);

  return (
    <DeskDndProvider>
      <div className={cn("h-full", concept.className)} data-concept={concept.id}>
        <concept.Shell
          sidebar={sidebar}
          sidebarCollapsed={collapsed}
          sidebarOverlay={narrow}
          onToggleSidebar={() => (narrow ? setNarrowOpen(!narrowOpen) : setCollapsed(!storedCollapsed))}
          detailPanel={detailPanel}
          slots={{
            containerActions: (n) => (
              <ContainerActions
                target={n.kind === "space" ? { kind: "space", space: n.space } : { kind: "project", project: n.project }}
                openCount={n.openCount}
              />
            ),
            areaActions: (n) => <ContainerActions target={{ kind: "area", area: n.area }} />,
            newArea: <NewAreaButton />,
            syncStatus: (
              <div className="flex min-w-0 items-center gap-0.5">
                <div className="min-w-0">
                  <SyncStatus />
                </div>
                <UpdateReady />
              </div>
            ),
            toolbar: <Toolbar />,
            onQuickAdd: () => openQuickAdd(),
            onSearch: () => ui.set({ paletteOpen: true }),
            settingsHref: href({ name: "settings" }),
            settingsActive: loc.route.name === "settings",
            sidebarDnd,
            rowMenu: (n, row) => (
              <ContainerContextMenu
                target={!("kind" in n) ? { kind: "area", area: n.area } : n.kind === "space" ? { kind: "space", space: n.space } : { kind: "project", project: n.project }}
                openCount={"kind" in n ? n.openCount : undefined}
              >
                {row}
              </ContainerContextMenu>
            ),
          }}
        >
          {boot.isLoading && !boot.data ? (
            <div className="h-40" aria-busy="true" />
          ) : boot.error && !boot.data ? (
            <concept.EmptyState
              message="Can't reach the Desk service. Changes you make are kept on this device."
              action={
                <Button size="sm" variant="outline" onClick={() => void boot.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : (
            <Fragment key={`${weekStart}|${timeFormat}`}>
              {/* Keyed by path so switching views fades the new one in (140ms); filters and tabs don't re-trigger it. */}
              <div key={loc.path} className="animate-fade-in">
                <Screen route={loc.route} />
              </div>
            </Fragment>
          )}
        </concept.Shell>
        <QuickAdd />
        <CommandPalette />
        <QuickMenu />
        <ShortcutsDialog />
        <TaskDetailOverlay />
        <ConflictDialog />
        <BulkBar />
      </div>
    </DeskDndProvider>
  );
}

/** A fresh Mac install opens on sign-in, so the first window isn't an empty workspace. */
export function WelcomeGate({ children }: { children: ReactNode }) {
  const needs = useNeedsWelcome();
  // Stay on the welcome screen through sign-in and the first sync, after `needs` turns false.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (needs) setOpen(true);
  }, [needs]);
  if (needs || open) return <MacWelcome onDone={() => setOpen(false)} />;
  if (needs === undefined) return <div className="h-full bg-background" />;
  return children;
}

/** Toasts follow the app's appearance, so they stay quiet in dark mode. */
function ThemedToaster() {
  const { dark } = useConcept();
  return <Toaster theme={dark ? "dark" : "light"} position="bottom-center" offset={20} gap={8} visibleToasts={3} />;
}

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [reason, setReason] = useState<string | undefined>();
  const qc = useMemo(() => createQueryClient(), []);

  const load = () => {
    setSession(undefined);
    void resolveSession().then(setSession);
  };
  useEffect(load, []);

  const api = useMemo(() => (session ? new ApiClient(session) : null), [session]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!api || !session) return;
    let unsub: (() => void) | undefined;
    const scope = `${session.apiUrl}`;
    void restoreCache(qc, scope).finally(() => {
      void outbox.init(api, qc);
      unsub = persistCache(qc, scope);
      setReady(true);
    });
    return () => unsub?.();
  }, [api, session, qc]);

  return (
    <QueryClientProvider client={qc}>
      <ConceptProvider>
        <TooltipProvider delayDuration={500} skipDelayDuration={200}>
          {session === undefined ? (
            <div className="h-full bg-background" />
          ) : session === null || !api ? (
            <SignInScreen reason={reason} onSignedIn={load} />
          ) : ready ? (
            <DeskProvider
              value={{
                api,
                session,
                signOut: () => {
                  if (session.source === "cloud") void signOutCloud(session.token).catch(() => {});
                  clearPairing();
                  clearCloudSession();
                  qc.clear();
                  setReason(undefined);
                  setSession(null);
                },
              }}
            >
              <WelcomeGate>
                <Workspace
                  onUnauthorized={() => {
                    if (session.source === "paired") {
                      clearPairing();
                      setReason("The saved token was rejected. Pair this device again.");
                      setSession(null);
                    } else if (session.source === "cloud") {
                      clearCloudSession();
                      setReason("You were signed out. Sign in again to keep going.");
                      setSession(null);
                    }
                  }}
                />
              </WelcomeGate>
            </DeskProvider>
          ) : (
            <div className="h-full bg-background" />
          )}
          <ThemedToaster />
        </TooltipProvider>
      </ConceptProvider>
    </QueryClientProvider>
  );
}
